import type { z } from 'zod';
import { type FieldTransformer, type TransformerConfig } from '../common/types';
import {
  createAbsentAsTransformer,
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId, decodeRecordId, encodeRecordId } from '../recordIds';
import { StudentStudentIdSchema } from '../students/schemas';
import { ClassSchema, DBClassSchema, DBClassStudentsEntrySchema } from './schemas';

type Class = z.infer<typeof ClassSchema>;
type DBClass = z.infer<typeof DBClassSchema>;

/** The day storage holds for a class whose day is undetermined. */
const UNDETERMINED_CLASS_DAY = -1;

/** The name a class goes by, `cl-{courseId}-{code}`, which its Slack channel also takes. */
export const getClassLabel = (cls: Pick<Class, 'courseId' | 'code'>) =>
  `cl-${cls.courseId}-${cls.code}`;

/**
 * A staff member's name copied beside their id: stored as `''` while unassigned, and absent on
 * the client while either it or the id beside it is.
 */
const createStaffNameTransformer = (
  staffField: 'teacher' | 'marker',
  nameField: 'teacherName' | 'markerName'
): FieldTransformer<typeof ClassSchema, typeof DBClassSchema> => ({
  encoder: createAbsentAsTransformer<typeof ClassSchema, typeof DBClassSchema>(nameField, '', {
    alwaysStored: true,
  }).encoder,
  decoder: {
    sourceFields: [staffField, nameField] as const,
    decode: data => {
      const name = data[nameField];
      return name === undefined || name === '' || data[staffField] === ''
        ? {}
        : { [nameField]: name };
    },
  },
});

export const ClassCodecConfig: TransformerConfig<
  typeof ClassSchema,
  typeof DBClassSchema,
  'id' | 'label'
> = {
  name: 'Class',
  clientSchema: ClassSchema,
  dbSchema: DBClassSchema,
  transformers: [
    createRenameTransformer<typeof ClassSchema, typeof DBClassSchema>('code', 'classId'),
    createRenameTransformer<typeof ClassSchema, typeof DBClassSchema>('courseId', 'course'),
    createRenameTransformer<typeof ClassSchema, typeof DBClassSchema>('streamId', 'stream'),
    createRenameTransformer<typeof ClassSchema, typeof DBClassSchema>(
      'permissionGroupId',
      'permissionGroup'
    ),
    createRenameTransformer<typeof ClassSchema, typeof DBClassSchema>('slackChannelId', 'slackId'),
    createRenameTransformer<typeof ClassSchema, typeof DBClassSchema>('teacherId', 'teacher', {
      absentAs: '',
      alwaysStored: true,
    }),
    createStaffNameTransformer('teacher', 'teacherName'),
    createRenameTransformer<typeof ClassSchema, typeof DBClassSchema>('markerId', 'marker', {
      absentAs: '',
      alwaysStored: true,
    }),
    createStaffNameTransformer('marker', 'markerName'),

    // roomId ↔ room: the room's code, its location being the class's own
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['roomId'] as const,
        encode: ({ roomId }) => ({ room: decodeRecordId('room', roomId as Class['roomId']).code }),
      },
      decoder: {
        sourceFields: ['location', 'room'] as const,
        decode: ({ location, room }) => ({
          ...(location !== undefined && { location }),
          ...(location !== undefined &&
            room !== undefined && { roomId: encodeRecordId('room', { location, code: room }) }),
        }),
      },
    }),

    // students: [{ studentId, name, schoolId, schoolLabel, schoolShortLabel, slackUserId }] ↔
    //   students: { studentId: { name, schoolId, schoolLabel, schoolShortLabel, slackId } }
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['students'] as const,
        encode: ({ students }) => ({
          students: (students as Class['students']).reduce(
            (
              acc: Record<
                z.infer<typeof StudentStudentIdSchema>,
                z.infer<typeof DBClassStudentsEntrySchema>
              >,
              student: Class['students'][number]
            ) => {
              acc[student.studentId] = {
                name: student.name,
                ...(student.schoolId ? { schoolId: student.schoolId } : {}),
                ...(student.schoolLabel ? { schoolLabel: student.schoolLabel } : {}),
                ...(student.schoolShortLabel ? { schoolShortLabel: student.schoolShortLabel } : {}),
                slackId: student.slackUserId,
              };
              return acc;
            },
            {} as Record<
              z.infer<typeof StudentStudentIdSchema>,
              z.infer<typeof DBClassStudentsEntrySchema>
            >
          ),
        }),
      },
      decoder: {
        sourceFields: ['students'] as const,
        decode: ({ students }) => ({
          students: Object.entries((students ?? {}) as NonNullable<DBClass['students']>).map(
            ([studentId, { slackId, ...student }]) => ({
              studentId,
              ...student,
              ...(slackId !== undefined && { slackUserId: slackId }),
            })
          ),
        }),
      },
    }),

    // activeDayTimeSortKey: computed from active, day and time, an undetermined day stored as
    // UNDETERMINED_CLASS_DAY. The day comes from the existing class rather than a dependency,
    // since a class whose day is undetermined has none to give.
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['active', 'day', 'time'] as const,
        dependencies: ['active', 'time'] as const,
        encode: (data, existing, context) => {
          const isDaySet = 'day' in data;
          const isWholeRecord = context.type === 'full';
          // An update that leaves the day as it is keys the class by the existing one
          if (!isDaySet && !isWholeRecord && !existing) {
            throw new Error('Missing dependency field in existing data: day');
          }
          const storedDay = (isDaySet ? data.day : existing?.day) ?? UNDETERMINED_CLASS_DAY;
          return {
            activeDayTimeSortKey: `${data.active}#${storedDay}#${data.time}`,
            active: data.active,
            time: data.time,
            ...((isDaySet || isWholeRecord) && { day: storedDay }),
          };
        },
      },
      decoder: {
        sourceFields: ['activeDayTimeSortKey', 'day'] as const,
        decode: ({ day }) => (day === undefined || day === UNDETERMINED_CLASS_DAY ? {} : { day }),
      },
    }),

    // activeStreamSortKey: computed from active, courseId and streamId
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['active', 'courseId', 'streamId'] as const,
        dependencies: ['active', 'courseId', 'streamId'] as const,
        encode: ({ active, courseId, streamId }) => ({
          activeStreamSortKey: `${active}#${courseId}#${streamId}`,
          active,
        }),
      },
      decoder: {
        sourceFields: ['activeStreamSortKey'] as const,
        decode: () => ({}),
      },
    }),

    // streamIds: the stream in use per course, kept in step with `courseId` and `streamId` on
    // every write that touches either. A map in the write replaces the stored one; without one
    // the entries for the class's other courses come from the map it already holds — the
    // existing class on a partial update, whose own course and stream stand in for a map it
    // was stored without — so a course change adds the new course's entry and keeps the old one
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['courseId', 'streamId', 'streamIds'] as const,
        dependencies: ['courseId', 'streamId'] as const,
        encode: ({ courseId, streamId, streamIds }, existing) => ({
          streams: {
            ...((streamIds as Class['streamIds']) ?? {
              ...existing?.streamIds,
              ...(existing?.courseId !== undefined && existing.streamId !== undefined
                ? { [existing.courseId]: existing.streamId }
                : {}),
            }),
            [courseId as Class['courseId']]: streamId as Class['streamId'],
          },
        }),
      },
      // A class stored before the map existed reads as holding the stream in use for its course
      decoder: {
        sourceFields: ['course', 'stream', 'streams'] as const,
        decode: ({ course, stream, streams }) => ({
          ...(course !== undefined ? { courseId: course } : {}),
          ...(stream !== undefined ? { streamId: stream } : {}),
          ...(streams !== undefined
            ? { streamIds: streams }
            : course !== undefined && stream !== undefined
              ? { streamIds: { [course as DBClass['course']]: stream as DBClass['stream'] } }
              : {}),
        }),
      },
    }),

    // slackChannelName: computed from courseId and code
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['courseId', 'code'] as const,
        dependencies: ['courseId', 'code'] as const,
        encode: ({ courseId, code }) => ({
          slackChannelName: getClassLabel({
            courseId: courseId as Class['courseId'],
            code: code as Class['code'],
          }),
        }),
      },
      decoder: {
        sourceFields: ['slackChannelName'] as const,
        decode: ({ slackChannelName }) => ({
          slackChannelName,
        }),
      },
    }),
  ],
  derived: {
    id: cls => buildRecordId('class', cls),
    label: cls =>
      cls.courseId !== undefined && cls.code !== undefined
        ? getClassLabel({ courseId: cls.courseId, code: cls.code })
        : undefined,
  },
};

export const encodeClassToDb = createEncodeToDbFn(ClassCodecConfig);
export const decodeClassFromDb = createDecodeFromDbFn(ClassCodecConfig);
